import React, { FC, useState, useEffect, useMemo } from 'react';
import { DropdownMenu, Dropdown } from 'reactstrap';
import _isEqual from 'lodash/isEqual';
import _includes from 'lodash/includes';
import _filter from 'lodash/filter';
import _findIndex from 'lodash/findIndex';
import _toLower from 'lodash/toLower';
import _isArrayLike from 'lodash/isArrayLike';
import styled from 'styled-components';
import Loader from '@minecraft.loader';
import { updateSelectAllOption } from '@minecraft.utils';

import Option from './Option';
import Section from './Section';
import SelectableSearchDropdownContext from './SelectableSearchDropdownContext';
import SelectAll from './SelectAll';
import Header from './Header';

const StyledDropdown = styled(Dropdown)`
  position: relative;

  &.show .dropdown-header {
    border-color: ${(props): string => props.theme.colors.primary.primary};
  }
`;
const StyledDropdownMenu = styled(({ $fullWidth, $maxHeight, ...restProps }) => <DropdownMenu {...restProps} />)`
  background-color: ${(props): string => props.theme.form.backgroundColor};
  border-color: ${(props): string => props.theme.form.borderColor};
  color: ${(props): string => props.theme.form.inputTextColor};
  max-height: ${(props) => props.$maxHeight};
  min-height: ${(props) => props.$maxHeight};
  overflow: auto;
  outline: none;
  margin-top: 0;
  ${(props): string => (props.$fullWidth ? 'width: 100%' : '')}
`;

const CheckboxesWrapper = styled.div`
  padding-left: 1rem;
`;

const LoaderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 2rem;
`;

export type Props = {
  children?: React.ReactNode;
  initialOptions: any[];
  values: any[];
  onChange?: (newOptions) => void;
  onOptionsFetch?: () => void;
  maxHeight?: string;
  resetValue?: any[];
  className?: string;
  name?: string;
  hideSelectAll?: boolean;
  saveAsOption?: boolean;
  isLoading?: boolean;
  ['data-qa-id']?: string;
  fullWidth?: boolean;
  lazyLoad?: boolean;
  isPlaceholderFontNormal?: boolean;
  toggleInputLabel?: string;
};

const SelectableSearchDropdownComponent: FC<Props> = ({
  children,
  initialOptions = [],
  resetValue,
  onChange,
  maxHeight = '12.5rem',
  className,
  name,
  hideSelectAll,
  saveAsOption = false,
  isLoading,
  lazyLoad,
  'data-qa-id': qaId,
  values = [], // <- values from state (onChange(checkbox check/uncheck) updates this)
  fullWidth = false,
  onOptionsFetch,
  isPlaceholderFontNormal,
  toggleInputLabel,
}: Props) => {
  // eslint-disable-next-line prefer-const
  const [isOpen, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState(values);
  const [allOptions, setAllOptions] = useState([]);
  const [filter, setFilter] = useState('');
  const [optionCount, setCount] = useState(lazyLoad ? 20 : null);

  const toggleDropdown = () => {
    if (!isOpen && onOptionsFetch) {
      onOptionsFetch();
    }

    setOpen(!isOpen);
  };

  // Update initialOptions when it changed
  useEffect(() => {
    if (initialOptions.length > 0) {
      setOptions(initialOptions);
    }
  }, [initialOptions]);

  // Initialize allOptions for select all component (deeply):
  useEffect(() => {
    updateSelectAllOption(children, setAllOptions);
  }, [children]);

  useEffect(() => {
    setOptions(values || []);
  }, [values]);

  const onScroll = lazyLoad
    ? (e) => {
        const scrollTop: number = e?.currentTarget?.scrollTop;
        const offsetHeight: number = e?.currentTarget?.offsetHeight;
        const scrollHeight: number = e?.currentTarget?.scrollHeight;

        if (scrollTop + offsetHeight >= scrollHeight) {
          setCount(optionCount + 20);
        }
      }
    : undefined;

  const memoizedValue = useMemo(() => {
    return {
      saveAsOption,
      options,
      setOptions: (newOptions) => {
        setOptions(newOptions);

        onChange(newOptions);
      },
      toggleOption: (option) => {
        setOptions((actualOptions) => {
          let newOptions;

          if (resetValue && _isEqual(options, resetValue)) {
            newOptions = [option];
          } else if (
            _findIndex(actualOptions, (actualOption) =>
              saveAsOption ? actualOption.value === option.value : actualOption === option
            ) !== -1
          ) {
            newOptions = _filter(actualOptions, (actualOption) =>
              saveAsOption ? actualOption.value !== option.value : actualOption !== option
            );
          } else {
            newOptions = [...actualOptions, option];
          }

          onChange(newOptions);

          return newOptions;
        });
      },
      filter,
      setFilter,
      visibilityPredicate: (label: string, filterInput: string, index: number): boolean =>
        filterInput || !lazyLoad ? _includes(_toLower(label), _toLower(filterInput)) : optionCount > index,
    };
  }, [saveAsOption, filter, lazyLoad, optionCount, options, resetValue]);

  return (
    <SelectableSearchDropdownContext.Provider value={memoizedValue}>
      <StyledDropdown
        isOpen={isOpen}
        toggle={toggleDropdown}
        className={className}
        data-qa-id={`${qaId}-selectable-dropdown-${name || ''}`}
      >
        <Header
          allOptions={allOptions}
          isPlaceholderFontNormal={isPlaceholderFontNormal}
          toggleInputLabel={toggleInputLabel}
        />
        <StyledDropdownMenu onScroll={onScroll} $fullWidth={fullWidth} $maxHeight={maxHeight}>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              {!hideSelectAll && <SelectAll allOptions={allOptions} />}
              <CheckboxesWrapper>{children}</CheckboxesWrapper>
              {lazyLoad && children && !filter && optionCount < (_isArrayLike(children) && children.length) && (
                <LoaderWrapper>
                  <Loader />
                </LoaderWrapper>
              )}
            </>
          )}
        </StyledDropdownMenu>
      </StyledDropdown>
    </SelectableSearchDropdownContext.Provider>
  );
};

const SelectableSearchDropdown: any = SelectableSearchDropdownComponent;

SelectableSearchDropdown.Option = Option;
SelectableSearchDropdown.Section = Section;

export default SelectableSearchDropdown;
